import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Calendar, User, Sparkles, AlertTriangle, Clock, FolderKanban, Share2, UserCheck,
  Loader2, Users, X, Plus, MessageSquare, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TASK_STATUS, isManager } from "@/lib/roles";

const selectCls = "w-full rounded-xl border border-[#e4d8c4] bg-white px-3 py-2.5 text-sm text-[#00334a]";

export function DueBadge({ task }) {
  if (task.status === "done" || !task.due_date) return null;
  if (task.is_overdue)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[#f6dcd0] px-2 py-0.5 text-[10px] font-bold uppercase text-[#b0442a]" data-testid="task-overdue-badge">
        <AlertTriangle className="h-3 w-3" /> Overdue
      </span>
    );
  if (task.due_soon)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[#f6ecd7] px-2 py-0.5 text-[10px] font-bold uppercase text-[#9c6a2e]" data-testid="task-duesoon-badge">
        <Clock className="h-3 w-3" /> Due soon
      </span>
    );
  return null;
}

const PERSON_STATUS = {
  open: { label: "Not started", cls: "bg-[#efe6d6] text-[#7c7a6a]" },
  in_progress: { label: "In progress", cls: "bg-[#e3eef0] text-[#3f6670]" },
  done: { label: "Done", cls: "bg-[#dcefe0] text-[#2f7d4f]" },
};

export default function TaskDetailDialog({ taskId, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const manager = isManager(user);
  const [task, setTask] = useState(null);
  const [vols, setVols] = useState([]);
  const [delegateTo, setDelegateTo] = useState("");
  const [addTo, setAddTo] = useState("");
  const [comments, setComments] = useState([]);
  const [cbody, setCbody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!taskId) return;
    api.get(`/tasks/${taskId}`).then(({ data }) => setTask(data)).catch(() => setTask(null));
  }, [taskId]);

  const loadComments = useCallback(() => {
    if (!taskId) return;
    api.get(`/tasks/${taskId}/comments`).then(({ data }) => setComments(data)).catch(() => setComments([]));
  }, [taskId]);

  useEffect(() => {
    if (!open) { setTask(null); setComments([]); return; }
    load();
    loadComments();
    setDelegateTo(""); setAddTo(""); setCbody("");
    api.get("/volunteers/list").then(({ data }) => setVols(data)).catch(() => setVols([]));
  }, [open, load, loadComments]);

  const refresh = () => { load(); onChanged?.(); };

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const changeStatus = (status) => run(() => api.post(`/tasks/${task.id}/status`, { status }), "Status updated");
  const delegate = () => {
    if (!delegateTo) { toast.error("Pick someone to delegate to"); return; }
    run(async () => { await api.post(`/tasks/${task.id}/delegate`, { assignee_id: delegateTo }); setDelegateTo(""); },
      "Task delegated — they've been notified");
  };
  const setAssignees = (ids, msg) => run(() => api.post(`/tasks/${task.id}/assign`, { assignee_ids: ids }), msg);

  const addComment = () => {
    const text = cbody.trim();
    if (!text) return;
    setBusy(true);
    api.post(`/tasks/${task.id}/comments`, { body: text })
      .then(() => { setCbody(""); loadComments(); })
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setBusy(false));
  };

  const aids = task?.assignee_ids || [];
  const assignees = task?.assignees || [];
  const isAssignee = aids.includes(user?.id);
  const myStatus = assignees.find((a) => a.id === user?.id)?.status || null;
  const canAct = manager || isAssignee;
  const activeStatus = isAssignee ? myStatus : task?.status;
  const delegateOptions = vols.filter((v) => !aids.includes(v.id));
  const addOptions = vols.filter((v) => !aids.includes(v.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="task-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading pr-6">{task ? task.title : "Task"}</DialogTitle>
          <DialogDescription className="sr-only">Task details, assignees, status, delegation and discussion</DialogDescription>
        </DialogHeader>

        {!task ? (
          <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#65949c]" /></div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ${TASK_STATUS[task.status]?.cls}`} data-testid="task-detail-status">
                {TASK_STATUS[task.status]?.label}
              </span>
              <DueBadge task={task} />
              {task.suited_role && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#efe6d6] px-2.5 py-0.5 text-[11px] font-bold text-[#3f6670]">
                  <Sparkles className="h-3 w-3" /> {task.suited_role_label}
                </span>
              )}
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-[#5a5849]"><FolderKanban className="h-4 w-4 text-[#65949c]" /> {task.project_name || "—"}</div>
              <div className="flex items-center gap-2 text-[#5a5849]" data-testid="task-detail-due">
                <Calendar className="h-4 w-4 text-[#c97049]" /> {task.due_date ? `Due ${task.due_date}` : "No due date set"}
              </div>
              {task.created_by_name && (
                <div className="flex items-center gap-2 text-[#7c7a6a]"><UserCheck className="h-4 w-4 text-[#87856c]" /> Created by {task.created_by_name}</div>
              )}
              {task.delegated_by_name && (
                <div className="flex items-center gap-2 text-[#7c7a6a]"><Share2 className="h-4 w-4 text-[#87856c]" /> Last delegated by {task.delegated_by_name}</div>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#7c7a6a]">
                <Users className="h-3.5 w-3.5" /> Assigned volunteers ({assignees.length})
              </div>
              <div className="mt-2 space-y-1.5" data-testid="task-detail-assignee">
                {assignees.length === 0 && <div className="text-sm text-[#7c7a6a]">Up for grabs — nobody assigned yet.</div>}
                {assignees.map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-2 rounded-xl border border-[#efe6d6] bg-white px-3 py-1.5" data-testid={`assignee-row-${a.id}`}>
                    <span className="truncate text-sm font-medium text-[#00334a]">{a.name}{a.id === user?.id ? " (you)" : ""}</span>
                    <span className="flex items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${PERSON_STATUS[a.status]?.cls}`}>{PERSON_STATUS[a.status]?.label}</span>
                      {manager && (
                        <button onClick={() => setAssignees(aids.filter((x) => x !== a.id), "Removed from task")} disabled={busy}
                          className="text-[#b0442a] hover:text-[#8a3420]" data-testid={`assignee-remove-${a.id}`} title="Remove">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
              {manager && (
                <div className="mt-2 flex items-center gap-2">
                  <select value={addTo} onChange={(e) => setAddTo(e.target.value)} className={selectCls} data-testid="assignee-add-select">
                    <option value="">Add a volunteer…</option>
                    {addOptions.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.role_label})</option>)}
                  </select>
                  <Button onClick={() => { if (addTo) { setAssignees([...aids, addTo], "Volunteer added"); setAddTo(""); } }}
                    disabled={busy || !addTo} className="shrink-0 rounded-full bg-[#00334a] font-bold hover:bg-[#00263a]" data-testid="assignee-add-button">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-[#7c7a6a]">Description</div>
              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-[#3a3a2f]" data-testid="task-detail-description">
                {task.description || "No description provided."}
              </p>
            </div>

            {task.delegate_note && (
              <div className="rounded-xl bg-[#f8f3e9] p-3 text-sm text-[#5a5849]">
                <span className="font-semibold text-[#00334a]">Hand-off note: </span>{task.delegate_note}
              </div>
            )}

            {canAct && (
              <div className="space-y-4 border-t border-[#efe6d6] pt-4">
                <div>
                  <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[#7c7a6a]">
                    {isAssignee ? "Your part of this task" : "Set status for everyone"}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {["open", "in_progress", "done"].map((s) => (
                      <Button key={s} size="sm" variant={activeStatus === s ? "default" : "outline"} disabled={busy}
                        onClick={() => changeStatus(s)}
                        className={`rounded-full font-semibold ${activeStatus === s ? "bg-[#00334a] hover:bg-[#00263a]" : "border-[#e4d8c4] text-[#3f6670]"}`}
                        data-testid={`task-detail-status-${s}`}>
                        {TASK_STATUS[s]?.label}
                      </Button>
                    ))}
                  </div>
                  {aids.length > 1 && (
                    <p className="mt-1 text-xs text-[#7c7a6a]">The task is marked done automatically once every assigned volunteer marks their part done.</p>
                  )}
                </div>

                <div>
                  <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[#7c7a6a]">Delegate / hand off</div>
                  <div className="flex items-center gap-2">
                    <select value={delegateTo} onChange={(e) => setDelegateTo(e.target.value)} className={selectCls} data-testid="task-delegate-select">
                      <option value="">Choose a volunteer…</option>
                      {delegateOptions.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.role_label})</option>)}
                    </select>
                    <Button onClick={delegate} disabled={busy || !delegateTo} className="shrink-0 rounded-full hf-btn-coral font-bold" data-testid="task-delegate-button">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delegate"}
                    </Button>
                  </div>
                  <p className="mt-1 text-xs text-[#7c7a6a]">Hands your spot to another volunteer and emails them.</p>
                </div>
              </div>
            )}

            <div className="border-t border-[#efe6d6] pt-4">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#7c7a6a]">
                <MessageSquare className="h-3.5 w-3.5" /> Discussion ({comments.length})
              </div>
              <div className="mt-2 space-y-2" data-testid="task-comments">
                {comments.length === 0 && <div className="text-sm text-[#7c7a6a]">No comments yet. Start the conversation.</div>}
                {comments.map((c) => (
                  <div key={c.id} className="rounded-xl bg-[#f8f3e9] p-3" data-testid={`comment-${c.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-[#00334a]">{c.user_name || "Volunteer"}</span>
                      <span className="text-[11px] text-[#9a9480]">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-[#3a3a2f]">{c.body}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-end gap-2">
                <textarea value={cbody} onChange={(e) => setCbody(e.target.value)} rows={2}
                  placeholder="Add a comment for the team…" data-testid="comment-input"
                  className="flex-1 resize-none rounded-xl border border-[#e4d8c4] bg-white px-3 py-2 text-sm text-[#00334a]" />
                <Button onClick={addComment} disabled={busy || !cbody.trim()} className="shrink-0 rounded-full bg-[#00334a] font-bold hover:bg-[#00263a]" data-testid="comment-submit">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
